import moment from "moment";
import { INCIDENT_CATEGORIES } from "pages/IncidentManagement/constants";

/**
 * The fortnight ending today.
 *
 * Care Watch reviews shifts that have already been worked and submitted, so
 * the window has to look backwards. Anchoring it to the start of the current
 * roster week ran it two weeks into the future instead, where no completed
 * shift can exist -- it excluded even last week's work, and the queue opened
 * empty however many signals were waiting.
 */
export const getCurrentFortnightRange = () => {
  const end = moment();
  return {
    fromDate: end.clone().subtract(13, "days").format("YYYY-MM-DD"),
    toDate: end.format("YYYY-MM-DD"),
  };
};

export const formatFortnightLabel = (fromDate, toDate) => {
  const from = moment(fromDate);
  const to = moment(toDate);
  if (!from.isValid() || !to.isValid()) return "";
  if (from.year() === to.year()) {
    return `${from.format("D MMM")} – ${to.format("D MMM YYYY")}`;
  }
  return `${from.format("D MMM YYYY")} – ${to.format("D MMM YYYY")}`;
};

export const CARE_WATCH_VIEWS = {
  INCIDENT: "incident",
  DOCUMENTATION: "documentation",
  LIVE: "live",
};

/**
 * Incident create timesheet picker (web + this constant’s counterpart on the app).
 *
 * Off for now so QA can test the form as it was: no dropdown, no required
 * timesheet check, and a blank create stays unlinked. Care Watch still sends
 * `timesheet_uuid` in the background and the report is auto-linked; the
 * dropdown stays hidden either way.
 *
 * Flip to true when the picker is ready to ship.
 */
export const SHOW_INCIDENT_TIMESHEET_PICKER = false;

export const isCareWatchLiveView = (view) => view === CARE_WATCH_VIEWS.LIVE;

/** Live API `shiftType` is `Scheduled` or `UnScheduled`. */
export const isUnscheduledLiveShift = (row = {}) => {
  const raw = String(row.shiftType || row.shift_type || "")
    .toLowerCase()
    .replace(/\s+/g, "");
  return raw === "unscheduled";
};

export const liveShiftTimeLabel = (row = {}) => {
  const label = String(row.shiftTimeLabel || "").trim();
  if (!isUnscheduledLiveShift(row)) return label;
  return label.replace(/^unscheduled\s*[·•-]\s*/i, "") || label;
};

export const isCareWatchSignalView = (view) =>
  view === CARE_WATCH_VIEWS.INCIDENT ||
  view === CARE_WATCH_VIEWS.DOCUMENTATION;

export const QUEUE_TABS = [
  {
    value: CARE_WATCH_VIEWS.INCIDENT,
    label: "Incident concerns",
    icon: "alert-triangle",
  },
  {
    value: CARE_WATCH_VIEWS.DOCUMENTATION,
    label: "Missed documentation",
    icon: "file-text",
  },
  {
    value: CARE_WATCH_VIEWS.LIVE,
    label: "Live shifts",
    icon: "live",
  },
];

export const VIEW_OPTIONS = QUEUE_TABS.filter((tab) =>
  isCareWatchSignalView(tab.value)
);

/** Live floor buckets. APIs will fill these; the UI is in place first. */
export const LIVE_STATES = {
  ALL: "all",
  IN_PROGRESS: "in_progress",
  NOT_STARTED: "not_started",
  RUNNING_LATE: "running_late",
  ON_FLOOR: "on_floor",
};

export const LIVE_KPI_KEYS = [
  LIVE_STATES.IN_PROGRESS,
  LIVE_STATES.NOT_STARTED,
  LIVE_STATES.RUNNING_LATE,
  LIVE_STATES.ON_FLOOR,
];

export const LIVE_STATE_BADGE = {
  [LIVE_STATES.IN_PROGRESS]: {
    label: "In progress",
    text: "#047857",
    bg: "#E6F4EE",
  },
  [LIVE_STATES.NOT_STARTED]: {
    label: "Not started",
    text: "#B45309",
    bg: "#F7F0E4",
  },
  [LIVE_STATES.RUNNING_LATE]: {
    label: "Running late",
    text: "#BE123C",
    bg: "#F8E8EC",
  },
};

export const LIVE_STATUS_OPTIONS = [
  { value: LIVE_STATES.ALL, label: "All" },
  { value: LIVE_STATES.IN_PROGRESS, label: "In progress" },
  { value: LIVE_STATES.NOT_STARTED, label: "Not started" },
  { value: LIVE_STATES.RUNNING_LATE, label: "Running late" },
];

/** Header help for the Readiness column — same score as timesheet AI. */
export const READINESS_COLUMN_HELP =
  "How ready this timesheet is for approval, from the AI analysis. 90% or more is passing. Lower scores need review before you approve.";

export const CARE_WATCH_STATUSES = {
  ALL: "all",
  NEEDS_FOLLOW_UP: "needs_follow_up",
  ACKNOWLEDGED: "acknowledged",
  PROMOTED: "promoted",
  OVERDUE: "overdue",
};

export const STATUS_OPTIONS = [
  { value: CARE_WATCH_STATUSES.ALL, label: "All" },
  { value: CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP, label: "Needs follow-up" },
  { value: CARE_WATCH_STATUSES.ACKNOWLEDGED, label: "Acknowledged" },
  { value: CARE_WATCH_STATUSES.PROMOTED, label: "Incident created" },
  { value: CARE_WATCH_STATUSES.OVERDUE, label: "Overdue" },
];

/** Missed documentation has no promote path — "Incident created" is incident-only. */
export const DOC_STATUS_OPTIONS = STATUS_OPTIONS.filter(
  (option) => option.value !== CARE_WATCH_STATUSES.PROMOTED
);

export const STATUS_LABELS = Object.fromEntries(
  STATUS_OPTIONS.map((option) => [option.value, option.label])
);

/** Same 3-month ceiling used by reporting, MAR, and other date filters. */
export const CARE_WATCH_MAX_RANGE_MONTHS = 3;

/** Cap `toDate` so the range is never longer than 3 calendar months. */
export const clampCareWatchToDate = (fromDate, toDate) => {
  if (!fromDate || !toDate) return toDate;
  const from = moment(fromDate, "YYYY-MM-DD", true);
  const to = moment(toDate, "YYYY-MM-DD", true);
  if (!from.isValid() || !to.isValid()) return toDate;
  const maxTo = from.clone().add(CARE_WATCH_MAX_RANGE_MONTHS, "months");
  return to.isAfter(maxTo) ? maxTo.format("YYYY-MM-DD") : toDate;
};

/** Monday–Sunday of the week that contains today. */
export const getCurrentWeekRange = () => {
  const start = moment().startOf("isoWeek");
  return {
    fromDate: start.format("YYYY-MM-DD"),
    toDate: start.clone().endOf("isoWeek").format("YYYY-MM-DD"),
  };
};

/**
 * The queue opens on the current week, on web and in the manager app.
 * Clearing filters returns to that week. The date control can still be widened.
 */
export const getDefaultFilters = () => ({
  view: CARE_WATCH_VIEWS.INCIDENT,
  ...getCurrentWeekRange(),
  category: [],
  risk: "all",
  issue: "all",
  liveState: LIVE_STATES.ALL,
  status: CARE_WATCH_STATUSES.NEEDS_FOLLOW_UP,
  search: "",
});

export const DEFAULT_DATE_RANGE = getCurrentFortnightRange();

export const CATEGORY_OPTIONS = [
  { value: "all", label: "All categories" },
  // Outcome radios. The Category cell shows only these words, so they are
  // filter values here and not incident-form checkboxes.
  { value: "hazard", label: "Hazard" },
  { value: "near_miss", label: "Near Miss" },
  ...INCIDENT_CATEGORIES,
];

export const RISK_LEVELS = {
  HIGH: "high",
  MODERATE: "moderate",
  LOW: "low",
  NOT_RATED: "not_rated",
};

export const RISK_OPTIONS = [
  { value: "all", label: "All risks" },
  { value: RISK_LEVELS.HIGH, label: "High" },
  { value: RISK_LEVELS.MODERATE, label: "Moderate" },
  { value: RISK_LEVELS.LOW, label: "Low" },
  { value: RISK_LEVELS.NOT_RATED, label: "Not rated" },
];

/** Choices a manager can set. The analysis never assigns Not rated. */
export const RISK_EDIT_OPTIONS = RISK_OPTIONS.filter((option) => option.value !== "all");

export const RISK_BADGE = {
  [RISK_LEVELS.HIGH]: {
    label: "High",
    text: "#BE123C",
    bg: "#F8E8EC",
  },
  [RISK_LEVELS.MODERATE]: {
    label: "Moderate",
    text: "#B45309",
    bg: "#F7F0E4",
  },
  [RISK_LEVELS.LOW]: {
    label: "Low",
    text: "#047857",
    bg: "#E6F4EE",
  },
  [RISK_LEVELS.NOT_RATED]: {
    label: "Not rated",
    text: "#475569",
    bg: "#F1F5F9",
  },
};

export const DOC_ISSUES = {
  MISSING_LOCATION: "missing_location",
  BRIEF_NOTE: "brief_note",
  GOALS_MISSING: "goals_missing",
  // The analysis also checks signatures, shift tasks and end-of-shift
  // questions. Without these the rows still arrive but fall back to the
  // wrong label, which reads as a note problem when it is not.
  MISSING_SIGNATURE: "missing_signature",
  TASKS_INCOMPLETE: "tasks_incomplete",
  END_QUESTIONS_INCOMPLETE: "end_questions_incomplete",
};

export const DOC_ISSUE_LABELS = {
  [DOC_ISSUES.MISSING_LOCATION]: "Missing location",
  [DOC_ISSUES.BRIEF_NOTE]: "Shift note",
  [DOC_ISSUES.GOALS_MISSING]: "Goals missing",
  [DOC_ISSUES.MISSING_SIGNATURE]: "Missing signature",
  [DOC_ISSUES.TASKS_INCOMPLETE]: "Shift tasks incomplete",
  [DOC_ISSUES.END_QUESTIONS_INCOMPLETE]: "End questions incomplete",
};

export const ISSUE_OPTIONS = [
  { value: "all", label: "All issues" },
  ...Object.entries(DOC_ISSUE_LABELS)
    .filter(([value]) => value !== DOC_ISSUES.MISSING_LOCATION)
    .map(([value, label]) => ({ value, label })),
];

/** KPI tile key → documentation `issue` filter sent to the API. */
export const KPI_ISSUE_BY_KEY = {
  brief: DOC_ISSUES.BRIEF_NOTE,
  goals: DOC_ISSUES.GOALS_MISSING,
  tasks: DOC_ISSUES.TASKS_INCOMPLETE,
  signature: DOC_ISSUES.MISSING_SIGNATURE,
  endQuestions: DOC_ISSUES.END_QUESTIONS_INCOMPLETE,
};

export const DEFAULT_FILTERS = getDefaultFilters();

export const PAGE_SIZE_OPTIONS = [10, 25, 50];
export const DEFAULT_PAGE_SIZE = 10;

export const CARE_WATCH_COLORS = {
  page: "#F3F5F8",
  surface: "#FFFFFF",
  primary: "#4B6CB7",
  primaryDark: "#293F6A",
  navy: "#02397A",
  soft: "#EEF2FA",
  text: "#111827",
  ink: "#0B1B33",
  muted: "#6B7280",
  subtle: "#9CA3AF",
  slate: "#64748B",
  border: "#E4E7EE",
  borderStrong: "#D5DAE3",
  thead: "#F6F7FB",
  selected: "#EEF2FA",
  warnBg: "#FBF6EC",
  warn: "#92400E",
};
